import { useFocusEffect, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  FileSearch,
  Plus,
  Scale,
  Search,
  Tag,
  Trash2,
  X,
} from 'lucide-react-native';
import { useCallback, useMemo, useState } from 'react';
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

import { useConfirm } from '@/components/ConfirmDialog';
import { HScroll } from '@/components/HScroll';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import type { QuoteRow, QuoteStatus } from '@/db/types';
import {
  QUOTE_STATUS_META,
  bulkDeleteQuotes,
  bulkSetQuoteStatus,
  expireLapsedQuotes,
  groupQuotesByProject,
  listQuotes,
} from '@/lib/quotes';
import { hapticHold, hapticSuccess } from '@/lib/haptics';
import { goBackOrTo } from '@/lib/nav';
import { getActiveProperty } from '@/lib/tasks';
import { webNoOutline } from '@/lib/web-style';
import { webTransition } from '@/lib/hover-style';

/**
 * Quotes — the "maybe money" ledger. Estimates cluster by project so
 * competing bids sit side-by-side with a visible price spread, and the
 * whole history stays browsable forever ("what did the roof cost to
 * quote in 2024?"). Converting a quote to an expense keeps the quote.
 */
export default function QuotesScreen() {
  const router = useRouter();
  const db = useSQLiteContext();
  const [items, setItems] = useState<QuoteRow[]>([]);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<QuoteStatus | 'all'>('all');
  // Mass edit (Wave 61): long-press a row to enter selection mode, tap
  // to toggle, then bulk-set status or bulk-delete from the sticky bar.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const selectMode = selected.size > 0;
  const [statusPickerOpen, setStatusPickerOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const confirm = useConfirm();
  const insets = useSafeAreaInsets();

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const applyBulkStatus = async (s: QuoteStatus) => {
    if (bulkBusy) return;
    setBulkBusy(true);
    try {
      await bulkSetQuoteStatus(db, [...selected], s);
      hapticSuccess();
      setStatusPickerOpen(false);
      clearSelection();
      await fetch();
    } finally {
      setBulkBusy(false);
    }
  };

  const applyBulkDelete = async () => {
    if (bulkBusy) return;
    const n = selected.size;
    const ok = await confirm({
      title: `Delete ${n} quote${n === 1 ? '' : 's'}?`,
      message: 'They disappear from your price history.',
      destructive: true,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    setBulkBusy(true);
    try {
      await bulkDeleteQuotes(db, [...selected]);
      hapticSuccess();
      clearSelection();
      await fetch();
    } finally {
      setBulkBusy(false);
    }
  };

  const fetch = useCallback(async () => {
    const prop = await getActiveProperty(db);
    if (!prop) return;
    // Truthful statuses before render — flips open→expired where
    // valid_until has lapsed. Cheap single UPDATE.
    await expireLapsedQuotes(db, prop.id);
    setItems(await listQuotes(db, prop.id));
  }, [db]);

  useFocusEffect(
    useCallback(() => {
      fetch();
    }, [fetch])
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (filterStatus !== 'all' && it.status !== filterStatus) return false;
      if (!q) return true;
      return (
        (it.vendor ?? '').toLowerCase().includes(q) ||
        (it.project ?? '').toLowerCase().includes(q) ||
        (it.description ?? '').toLowerCase().includes(q) ||
        it.category.toLowerCase().includes(q)
      );
    });
  }, [items, search, filterStatus]);

  const groups = useMemo(() => groupQuotesByProject(filtered), [filtered]);

  // Summary strip numbers — always computed against ALL quotes (not the
  // filtered view) so the totals stay stable while filtering.
  const openCount = items.filter((i) => i.status === 'open').length;
  const openCents = items
    .filter((i) => i.status === 'open')
    .reduce((s, i) => s + i.amount_cents, 0);
  const convertedCount = items.filter((i) => i.status === 'converted').length;

  const money = (cents: number) => `$${(cents / 100).toLocaleString()}`;

  const STATUS_FILTERS: (QuoteStatus | 'all')[] = [
    'all', 'open', 'accepted', 'declined', 'expired', 'converted',
  ];

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable
          onPress={() => goBackOrTo(router, '/(tabs)')}
          style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
          hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Quotes</Text>
        <View style={{ flexDirection: 'row', gap: 6 }}>
          {/* Compare sits right beside Add so bid comparison is
              discoverable, not buried (Wave 61, user call). */}
          <Pressable
            onPress={() => router.push('/quotes/compare' as never)}
            style={({ pressed }) => [styles.addOutline, pressed && { opacity: 0.85 }]}
            accessibilityLabel="Compare bids">
            <Scale size={16} color={Palette.forest} strokeWidth={2} />
          </Pressable>
          <Pressable
            onPress={() => router.push('/quotes/new' as never)}
            style={({ pressed }) => [styles.add, pressed && { opacity: 0.85 }]}
            accessibilityLabel="New quote">
            <Plus size={18} color={Palette.cream} strokeWidth={2.2} />
          </Pressable>
        </View>
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 100 }} showsVerticalScrollIndicator={false}>
        {/* Summary strip — open pipeline + lifetime record. */}
        <View style={styles.summaryCard}>
          <View style={styles.summaryCell}>
            <Text style={styles.summaryValue}>{openCount}</Text>
            <Text style={styles.summaryLabel}>OPEN</Text>
          </View>
          <View style={styles.summaryDivider} />
          <View style={styles.summaryCell}>
            <Text style={styles.summaryValue}>{money(openCents)}</Text>
            <Text style={styles.summaryLabel}>ON THE TABLE</Text>
          </View>
          <View style={styles.summaryDivider} />
          <View style={styles.summaryCell}>
            <Text style={styles.summaryValue}>{convertedCount}</Text>
            <Text style={styles.summaryLabel}>BECAME EXPENSES</Text>
          </View>
        </View>

        {/* Compare promo — only when there's actually something to
            compare. One tap to the side-by-side view. */}
        {groups.some((g) => g.project && g.quotes.length > 1) ? (
          <Pressable
            onPress={() => router.push('/quotes/compare' as never)}
            style={({ pressed }) => [styles.comparePromo, pressed && { opacity: 0.9 }]}>
            <Scale size={16} color={Palette.forest} strokeWidth={2} />
            <Text style={styles.comparePromoText}>
              {groups.filter((g) => g.project && g.quotes.length > 1).length} project
              {groups.filter((g) => g.project && g.quotes.length > 1).length === 1 ? ' has' : 's have'} competing
              bids — compare them side by side
            </Text>
            <ChevronRight size={15} color={Palette.forest} strokeWidth={2.2} />
          </Pressable>
        ) : null}

        {items.length > 0 ? (
          <>
            <View style={styles.searchWrap}>
              <Search size={14} color={Palette.inkFaint} strokeWidth={2} />
              <TextInput
                value={search}
                onChangeText={setSearch}
                placeholder="Search vendor, project, work…"
                placeholderTextColor={Palette.inkFaint}
                style={[styles.searchInput, webNoOutline]}
              />
            </View>

            <HScroll contentStyle={styles.chipRow}>
              {STATUS_FILTERS.map((s) => {
                const sel = filterStatus === s;
                const label = s === 'all' ? 'All' : QUOTE_STATUS_META[s].label;
                return (
                  <Pressable
                    key={s}
                    onPress={() => setFilterStatus(s)}
                    style={({ pressed }) => [
                      styles.chip,
                      sel && styles.chipSel,
                      pressed && { opacity: 0.85 },
                    ]}>
                    <Text style={[styles.chipText, sel && styles.chipTextSel]}>{label}</Text>
                  </Pressable>
                );
              })}
            </HScroll>
          </>
        ) : null}

        {items.length === 0 ? (
          <View style={styles.empty}>
            <View style={styles.emptyIcon}>
              <FileSearch size={28} color={Palette.honeyDeep} strokeWidth={1.7} />
            </View>
            <Text style={styles.emptyTitle}>Keep every estimate</Text>
            <Text style={styles.emptyText}>
              Getting bids for a roof, a furnace, a fence? Save each quote here —
              scan the paper estimate and we&apos;ll read the vendor and price.
              Group competing bids by project to compare, and years from now
              you&apos;ll know exactly what work used to cost.
            </Text>
            <Pressable
              onPress={() => router.push('/quotes/new' as never)}
              style={({ pressed }) => [styles.emptyCta, pressed && { transform: [{ scale: 0.97 }] }]}>
              <Plus size={14} color={Palette.cream} strokeWidth={2.4} />
              <Text style={styles.emptyCtaText}>Add your first quote</Text>
            </Pressable>
          </View>
        ) : (
          groups.map((g) => (
            <View key={g.project ?? g.quotes[0].id} style={styles.group}>
              {/* Project header only when there IS a project. Multi-bid
                  groups get a spread badge so the min–max range reads at
                  a glance. */}
              {g.project ? (
                <View style={styles.groupHead}>
                  <Text style={styles.groupTitle} numberOfLines={1}>{g.project}</Text>
                  <Text style={styles.groupSpread}>
                    {g.quotes.length > 1
                      ? `${g.quotes.length} bids · ${money(g.minCents)}–${money(g.maxCents)}`
                      : money(g.minCents)}
                  </Text>
                </View>
              ) : null}
              {g.quotes.map((q, i) => {
                const meta = QUOTE_STATUS_META[q.status];
                const cheapest = g.quotes.length > 1 && i === 0;
                return (
                  <Pressable
                    key={q.id}
                    onPress={() =>
                      selectMode
                        ? toggleSelect(q.id)
                        : router.push(`/quotes/${q.id}` as never)
                    }
                    onLongPress={() => {
                      if (!selectMode) hapticHold();
                      toggleSelect(q.id);
                    }}
                    delayLongPress={280}
                    style={({ pressed, hovered }) => [
                      styles.row,
                      webTransition,
                      hovered && styles.rowHover,
                      selected.has(q.id) && styles.rowSelected,
                      pressed && { opacity: 0.85 },
                    ]}>
                    {selected.has(q.id) ? (
                      <View style={styles.selTick}>
                        <Check size={13} color={Palette.cream} strokeWidth={2.6} />
                      </View>
                    ) : null}
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <View style={styles.rowTitleLine}>
                        <Text style={styles.rowTitle} numberOfLines={1}>
                          {q.vendor || q.description || q.category}
                        </Text>
                        {cheapest ? (
                          <View style={styles.lowBadge}>
                            <Text style={styles.lowBadgeText}>LOWEST</Text>
                          </View>
                        ) : null}
                      </View>
                      <Text style={styles.rowMeta} numberOfLines={1}>
                        {[
                          new Date(q.quoted_on).toLocaleDateString(undefined, {
                            month: 'short', day: 'numeric', year: 'numeric',
                          }),
                          q.category,
                          !g.project && q.description ? q.description : null,
                        ].filter(Boolean).join(' · ')}
                      </Text>
                    </View>
                    <View style={styles.rowRight}>
                      <Text style={styles.rowAmount}>{money(q.amount_cents)}</Text>
                      <View style={[styles.statusPill, { borderColor: meta.tint }]}>
                        <Text style={[styles.statusPillText, { color: meta.tint }]}>
                          {meta.label}
                        </Text>
                      </View>
                    </View>
                    <ChevronRight size={14} color={Palette.inkFaint} strokeWidth={1.7} />
                  </Pressable>
                );
              })}
            </View>
          ))
        )}
      </ScrollView>

      {/* Mass-edit action bar (Wave 61). */}
      {selectMode ? (
        <View style={[styles.bulkBar, { bottom: Math.max(14, insets.bottom + 10) }]}>
          <Text style={styles.bulkCount}>{selected.size} selected</Text>
          <Pressable
            onPress={() => setStatusPickerOpen(true)}
            disabled={bulkBusy}
            style={({ pressed }) => [styles.bulkBtn, pressed && { opacity: 0.85 }]}>
            <Tag size={14} color={Palette.cream} strokeWidth={2} />
            <Text style={styles.bulkBtnText}>Status</Text>
          </Pressable>
          <Pressable
            onPress={applyBulkDelete}
            disabled={bulkBusy}
            style={({ pressed }) => [styles.bulkBtn, styles.bulkBtnDanger, pressed && { opacity: 0.85 }]}>
            <Trash2 size={14} color={Palette.cream} strokeWidth={2} />
            <Text style={styles.bulkBtnText}>Delete</Text>
          </Pressable>
          <Pressable
            onPress={clearSelection}
            hitSlop={8}
            accessibilityLabel="Clear selection"
            style={({ pressed }) => [styles.bulkClose, pressed && { opacity: 0.8 }]}>
            <X size={16} color={Palette.cream} strokeWidth={2.2} />
          </Pressable>
        </View>
      ) : null}

      <Modal transparent visible={statusPickerOpen} animationType="fade" onRequestClose={() => setStatusPickerOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setStatusPickerOpen(false)}>
          <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>
              Mark {selected.size} quote{selected.size === 1 ? '' : 's'} as…
            </Text>
            {(['open', 'accepted', 'declined', 'expired'] as QuoteStatus[]).map((s) => (
              <Pressable
                key={s}
                onPress={() => void applyBulkStatus(s)}
                disabled={bulkBusy}
                style={({ pressed }) => [styles.modalRow, pressed && { backgroundColor: '#EEF3EE' }]}>
                <Text style={[styles.modalRowText, { color: QUOTE_STATUS_META[s].tint }]}>
                  {QUOTE_STATUS_META[s].label}
                </Text>
              </Pressable>
            ))}
            <Text style={styles.modalHint}>
              Converted quotes keep their status — that state only comes
              from turning a quote into an expense.
            </Text>
          </Pressable>
        </Pressable>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  addOutline: {
    width: 36, height: 36, borderRadius: 18,
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    alignItems: 'center', justifyContent: 'center',
  },
  comparePromo: {
    flexDirection: 'row', alignItems: 'center', gap: 9,
    marginTop: Spacing.three, marginHorizontal: Spacing.four,
    paddingHorizontal: 13, paddingVertical: 11,
    borderRadius: 13,
    backgroundColor: '#EEF3EE',
    borderWidth: 1.5, borderColor: 'rgba(44,71,56,0.25)',
  },
  comparePromoText: {
    flex: 1,
    fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.forest,
    lineHeight: 17,
  },
  rowSelected: { borderColor: Palette.forest, backgroundColor: '#EEF3EE' },
  selTick: {
    width: 24, height: 24, borderRadius: 8,
    backgroundColor: Palette.forest,
    alignItems: 'center', justifyContent: 'center',
    marginRight: 4,
  },
  bulkBar: {
    position: 'absolute', left: 14, right: 14,
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: Palette.forest, borderRadius: 16,
    paddingHorizontal: 14, paddingVertical: 12,
    shadowColor: '#000', shadowOpacity: 0.22, shadowRadius: 12,
    shadowOffset: { width: 0, height: 4 }, elevation: 8,
  },
  bulkCount: { flex: 1, fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.cream },
  bulkBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 5,
    backgroundColor: 'rgba(251,246,234,0.16)',
    paddingHorizontal: 12, paddingVertical: 8, borderRadius: 10,
  },
  bulkBtnDanger: { backgroundColor: 'rgba(176,95,60,0.55)' },
  bulkBtnText: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.cream },
  bulkClose: { padding: 6 },
  modalBackdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  modalCard: {
    backgroundColor: Palette.sand, borderRadius: 24,
    padding: 18, width: '100%', maxWidth: 380,
  },
  modalTitle: {
    fontFamily: Fonts.serifBold, fontSize: 17, color: Palette.ink, marginBottom: 10,
  },
  modalRow: { paddingVertical: 11, paddingHorizontal: 12, borderRadius: 10 },
  modalRowText: { fontFamily: Fonts.sansBold, fontSize: 13.5 },
  modalHint: {
    marginTop: 8,
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft,
    lineHeight: 16,
  },
  add: {
    width: 36, height: 36, borderRadius: 18,
    backgroundColor: Palette.forest,
    alignItems: 'center', justifyContent: 'center',
  },
  summaryCard: {
    marginTop: Spacing.two, marginHorizontal: Spacing.four,
    flexDirection: 'row', alignItems: 'stretch',
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.xl, paddingVertical: Spacing.three,
  },
  summaryCell: { flex: 1, alignItems: 'center', gap: 2 },
  summaryDivider: { width: 1, backgroundColor: Palette.line },
  summaryValue: {
    fontFamily: Fonts.serifBold, fontSize: 18, color: Palette.forest,
    letterSpacing: -0.3,
  },
  summaryLabel: {
    fontFamily: Fonts.sansBold, fontSize: 10.5, color: Palette.inkFaint,
    letterSpacing: 0.6,
  },
  searchWrap: {
    marginTop: Spacing.three, marginHorizontal: Spacing.four,
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    borderRadius: 999, paddingHorizontal: Spacing.three,
  },
  searchInput: {
    flex: 1, paddingVertical: 9,
    fontFamily: Fonts.sansMedium, fontSize: 13, color: Palette.ink,
  },
  chipRow: {
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, gap: 6,
  },
  chip: {
    paddingHorizontal: 12, paddingVertical: 7, borderRadius: 999,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
  },
  chipSel: { backgroundColor: Palette.forest, borderColor: Palette.forest },
  chipText: { fontFamily: Fonts.sansBold, fontSize: 12.5, color: Palette.inkSoft },
  chipTextSel: { color: Palette.cream },
  group: {
    marginTop: Spacing.three, marginHorizontal: Spacing.four,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.xl, overflow: 'hidden',
  },
  groupHead: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    gap: 8,
    paddingHorizontal: Spacing.four, paddingVertical: 10,
    backgroundColor: '#EAF0EA',
    borderBottomWidth: 1, borderBottomColor: Palette.line,
  },
  groupTitle: {
    flex: 1,
    fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.forest,
    letterSpacing: 0.1,
  },
  groupSpread: {
    fontFamily: Fonts.sansBold, fontSize: 12, color: Palette.inkSoft,
  },
  row: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    paddingHorizontal: Spacing.four, paddingVertical: 12,
    borderBottomWidth: 1, borderBottomColor: Palette.line,
  },
  rowHover: { backgroundColor: '#FBF6EA' },
  rowTitleLine: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  rowTitle: {
    fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink,
    letterSpacing: -0.05, flexShrink: 1,
  },
  lowBadge: {
    paddingHorizontal: 5, paddingVertical: 2, borderRadius: 5,
    backgroundColor: '#DCE7E1',
  },
  lowBadgeText: {
    fontFamily: Fonts.sansBold, fontSize: 8.5, color: Palette.forest,
    letterSpacing: 0.5,
  },
  rowMeta: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft,
  },
  rowRight: { alignItems: 'flex-end', gap: 3 },
  rowAmount: {
    fontFamily: Fonts.serifBold, fontSize: 15, color: Palette.ink,
    letterSpacing: -0.2,
  },
  statusPill: {
    paddingHorizontal: 6, paddingVertical: 2, borderRadius: 999,
    borderWidth: 1,
  },
  statusPillText: {
    fontFamily: Fonts.sansBold, fontSize: 10.5, letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  empty: {
    alignItems: 'center', paddingVertical: Spacing.eight,
    paddingHorizontal: Spacing.five, gap: Spacing.three,
  },
  emptyIcon: {
    width: 64, height: 64, borderRadius: 32,
    backgroundColor: Palette.honeySoft,
    alignItems: 'center', justifyContent: 'center',
  },
  emptyTitle: { fontFamily: Fonts.serifBold, fontSize: 20, color: Palette.ink },
  emptyText: {
    fontFamily: Fonts.sansMedium, fontSize: 13.5, lineHeight: 19,
    color: Palette.inkSoft, textAlign: 'center', maxWidth: 460,
  },
  emptyCta: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingHorizontal: Spacing.four, paddingVertical: 10,
    borderRadius: 999, backgroundColor: Palette.forest, marginTop: Spacing.two,
  },
  emptyCtaText: {
    fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.cream,
    letterSpacing: 0.2,
  },
});
